import { useState, useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  MousePointerClick,
  MessageCircleQuestion,
} from "lucide-react";
import { Kicker } from "@/components/ui/kicker";
import { Tonal } from "@/components/ui/tonal";
import { VoiceProse } from "@/components/ui/voice-prose";
import { toneBorderClass } from "@/components/experience/vocabulary";
import { cn } from "@/lib/utils";
import { formatElapsedSeconds } from "./live-step-timer";

export function GhostStep({
  stepNumber,
  action,
}: {
  stepNumber: number;
  action: {
    stepNumber: number;
    actionType: string;
    selector: string | null;
    startedAt?: number;
  };
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!action.startedAt) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [action.startedAt]);

  const elapsedSec = action.startedAt
    ? Math.max(0, Math.floor((now - action.startedAt) / 1000))
    : 0;

  const isAsk =
    action.actionType === "ask_user" || action.actionType === "operator_setup";
  if (isAsk) {
    return (
      <li className="border-t border-border/60 pt-7 first:border-t-0 first:pt-0">
        <Tonal
          padding="md"
          className="space-y-2 border-l-[3px] border-severity-important"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <MessageCircleQuestion
                className="h-3.5 w-3.5 animate-pulse text-severity-important"
                aria-hidden="true"
              />
              <Kicker className="text-severity-important">
                {action.actionType === "operator_setup"
                  ? "Waiting for setup"
                  : "Waiting for operator"}
              </Kicker>
            </div>
            {action.startedAt && (
              <span className="font-mono text-xs tabular-nums text-muted-foreground shrink-0">
                {formatElapsedSeconds(elapsedSec)}
              </span>
            )}
          </div>
          {action.selector && (
            <p className="text-base text-foreground">{action.selector}</p>
          )}
        </Tonal>
      </li>
    );
  }

  const label = actionLabel(action.actionType, action.selector);
  return (
    <li className="border-t border-border/60 pt-7 first:border-t-0 first:pt-0">
      <div className="flex items-baseline gap-3">
        <span className="w-6 shrink-0 text-sm font-medium tabular-nums text-muted-foreground">
          {String(stepNumber).padStart(2, "0")}
        </span>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <MousePointerClick
                className="h-3.5 w-3.5 shrink-0 animate-pulse text-primary"
                aria-hidden="true"
              />
              <span className="truncate text-base text-muted-foreground">
                {label}
              </span>
            </div>
            {action.startedAt && (
              <span className="font-mono text-xs tabular-nums text-muted-foreground shrink-0">
                {formatElapsedSeconds(elapsedSec)}
              </span>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

export function SilentPresenceCue() {
  return (
    <p aria-live="polite" className="mt-3 pl-9 text-sm text-muted-foreground">
      <span className="mr-2 inline-flex gap-1 align-middle">
        <span className="inline-block h-[4px] w-[4px] animate-pulse rounded-full bg-muted-foreground [animation-delay:0ms]" />
        <span className="inline-block h-[4px] w-[4px] animate-pulse rounded-full bg-muted-foreground [animation-delay:200ms]" />
        <span className="inline-block h-[4px] w-[4px] animate-pulse rounded-full bg-muted-foreground [animation-delay:400ms]" />
      </span>
      watching the screen
    </p>
  );
}

function actionLabel(type: string, selector: string | null): ReactNode {
  switch (type) {
    case "click":
      return selector ? `Clicking "${selector}"` : "Clicking";
    case "fill":
      return selector ? `Filling "${selector}"` : "Filling field";
    case "navigate":
      return selector ? `Navigating to ${selector}` : "Navigating";
    case "scroll":
      return `Scrolling${selector ? ` ${selector}` : " down"}`;
    default:
      return type;
  }
}

export function HumanTestBlock({
  completion,
  projectId,
  walkthroughId,
}: {
  completion: {
    terminationReason: string;
    verdict: { question: string; answer: string } | null;
    whatWorked: string[];
    openQuestions: string[];
  };
  projectId: string;
  walkthroughId: string;
}) {
  const tone: Parameters<typeof toneBorderClass>[0] =
    completion.terminationReason === "error"
      ? "critical"
      : completion.terminationReason === "max_steps"
        ? "important"
        : completion.terminationReason === "persona_done"
          ? "helps"
          : "muted";

  return (
    <section className="space-y-4">
      <div className="flex items-baseline justify-between gap-4">
        <Kicker as="h2" className="text-foreground">
          The human test
        </Kicker>
      </div>
      {completion.verdict && (
        <div className="space-y-3">
          <h3 className="text-md font-semibold">
            {completion.verdict.question}
          </h3>
          <div
            className={cn(
              "border-l-[3px] bg-surface-tonal px-5 py-4",
              toneBorderClass(tone),
            )}
          >
            <VoiceProse className="text-foreground">
              {completion.verdict.answer}
            </VoiceProse>
          </div>
        </div>
      )}

      {completion.whatWorked.length > 0 && (
        <section className="space-y-3">
          <Kicker as="h3">What worked</Kicker>
          <ul className="space-y-2">
            {completion.whatWorked.map((item) => (
              <li key={item} className="flex items-baseline gap-3 text-base">
                <span
                  aria-hidden="true"
                  className="inline-block size-1.5 shrink-0 -translate-y-0.5 rounded-full bg-mark-helps"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {completion.openQuestions.length > 0 && (
        <section className="space-y-3">
          <Kicker as="h3">Open questions</Kicker>
          <ul className="space-y-2">
            {completion.openQuestions.map((item) => (
              <li key={item} className="text-base">
                {item}
              </li>
            ))}
          </ul>
        </section>
      )}

      <Link
        to={`/projects/${projectId}/walkthroughs/${walkthroughId}`}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
      >
        View the full report
        <ArrowRight className="h-3 w-3" aria-hidden="true" />
      </Link>
    </section>
  );
}

export function ErrorBlock({ message }: { message: string }) {
  return (
    <section className="space-y-4">
      <Kicker as="h2" className="text-severity-critical">
        Walkthrough stopped
      </Kicker>
      <div className="border-l-[3px] border-severity-critical bg-surface-tonal px-5 py-4">
        <p className="text-base text-foreground">{message}</p>
      </div>
    </section>
  );
}
